import './Predictions.scss';
import { useTranslator } from '@Localization';
import { LatLng } from 'leaflet';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Tooltip } from 'react-leaflet';
import { v4 } from 'uuid';
import { RectangleFeature } from '../../../../__legacy__/modules/annotation/components/features/RectangleFeature';
import { RotatedRectangleFeature } from '../../../../__legacy__/modules/annotation/components/features/RotatedRectangleFeature';
import { AnnotationToolType } from '../../../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { S_AnnotationStatus } from '../../../annotations/annotationStatus/S_AnnotationStatus';
import { IRectanglePrediction } from '../../jobPredictions.model';

interface IProps {
  predictedRectangles?: IRectanglePrediction[] | null;
  threshold: number;
  predictionClassIds: string[];
  zoomLevel: number;
}

export const PredictedRectangles = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const renderKey = v4();
  const { predictedRectangles, predictionClassIds, threshold } = props;

  if (!predictedRectangles) {
    return <></>;
  }

  return (
    <>
      {predictedRectangles.map((predictedClass) => {
        if (!predictionClassIds.includes(predictedClass.classId)) {
          return <></>;
        }

        return predictedClass.rectangles.map((rectangle, key) => {
          if (rectangle.score < threshold || !predictedClass.color) {
            return <></>;
          }

          switch (predictedClass.selectorType) {
            case AnnotationToolType.ROTATEDRECTANGLE:
              return (
                <RotatedRectangleFeature
                  isActive={false}
                  key={`${predictedClass.className}-${renderKey}-${key}`}
                  feature={{
                    id: `${predictedClass.className}-${renderKey}-${key}`,
                    color: predictedClass.color,
                    featureType: AnnotationToolType.ROTATEDRECTANGLE,
                    type: 'Feature',
                    geometry: {
                      type: 'Polygon',
                      coordinates: [
                        [
                          [rectangle.vertices[0].lat, rectangle.vertices[0].lng],
                          [rectangle.vertices[1].lat, rectangle.vertices[1].lng],
                          [rectangle.vertices[2].lat, rectangle.vertices[2].lng],
                          [rectangle.vertices[3].lat, rectangle.vertices[3].lng],
                          [rectangle.vertices[0].lat, rectangle.vertices[0].lng],
                        ],
                      ],
                    },
                    properties: {
                      annotationTypeId: predictedClass.classId,
                      featureType: AnnotationToolType.ROTATEDRECTANGLE,
                    },
                  }}
                  latlngs={rectangle.vertices as LatLng[]}
                  opacity={1}
                  fillOpacity={0.2}
                  onSegmentationOver={() => null}
                  onSegmentationOut={() => null}
                  onClick={() => null}
                  onContextMenu={() => null}
                  className='with-context-menu'
                  zoomLevel={props.zoomLevel}
                  dashArray={'4 8'}
                  tooltip={
                    <Tooltip
                      className='rectangle-tooltip'
                      opacity={1}
                    >
                      <div className='rectangle-container'>
                        <span className='rectangle-value'>
                          {t('class')}: <S_AnnotationStatus text={predictedClass.className} />
                        </span>
                      </div>
                      <div className='rectangle-container'>
                        <span className='rectangle-value'>
                          {t('score')}: <S_AnnotationStatus text={rectangle.score.toFixed(2)} />
                        </span>
                      </div>
                    </Tooltip>
                  }
                />
              );
            case AnnotationToolType.RECTANGLE:
              return (
                <RectangleFeature
                  isActive={false}
                  positions={rectangle.vertices}
                  key={`${predictedClass.className}-${renderKey}-${key}`}
                  storeKey={`${predictedClass.className}-${renderKey}-${key}`}
                  color={predictedClass.color}
                  type={AnnotationToolType.ROTATEDRECTANGLE}
                  canRotate={false}
                  dashArray={'4 8'}
                  opacity={1}
                  fillOpacity={0.2}
                  tooltip={
                    <Tooltip
                      className='rectangle-tooltip'
                      opacity={1}
                    >
                      <div className='rectangle-container'>
                        <span className='rectangle-value'>
                          {t('class')}: <S_AnnotationStatus text={predictedClass.className} />
                        </span>
                      </div>
                      <div className='rectangle-container'>
                        <span className='rectangle-value'>
                          {t('score')}: <S_AnnotationStatus text={rectangle.score.toFixed(2)} />
                        </span>
                      </div>
                    </Tooltip>
                  }
                />
              );
            case AnnotationToolType.SELECT:
            case AnnotationToolType.POINT:
            case AnnotationToolType.POLYGON:
            case AnnotationToolType.VECTOR:
            case AnnotationToolType.POLYLINE:
            case AnnotationToolType.BRUSH:
            default:
              return <></>;
          }
        });
      })}
    </>
  );
});
